/**
 * Ressort physique minimal, paramétré comme chez Apple : `amortissement` (1 = critique, aucun
 * dépassement ; < 1 = léger rebond) et `reponse` (en secondes, rapidité à rejoindre la cible —
 * ce n'est PAS une durée : le temps de stabilisation découle des deux paramètres).
 *
 * Toujours lancé depuis la valeur AFFICHÉE et avec la vitesse du doigt au relâché : c'est ce qui
 * rend l'animation interruptible et sans couture avec le geste qui la précède.
 *
 * @returns {() => void} fonction d'arrêt (à appeler si l'utilisateur rattrape l'élément).
 */
export function animerRessort({ de, vers, vitesse = 0, reponse = 0.4, amortissement = 1, surImage, surFin }) {
  const raideur = Math.pow((2 * Math.PI) / reponse, 2);
  const frottement = (4 * Math.PI * amortissement) / reponse;
  let x = de;
  let v = vitesse;
  let precedent = null;
  let id = null;
  let arrete = false;

  function pas(t) {
    if (arrete) return;
    const dt = precedent == null ? 1 / 60 : Math.min((t - precedent) / 1000, 1 / 30);
    precedent = t;
    // Sous-pas fixes : stable même si une image saute (tablette chargée).
    const n = 4;
    for (let i = 0; i < n; i += 1) {
      const a = -raideur * (x - vers) - frottement * v;
      v += a * (dt / n);
      x += v * (dt / n);
    }
    if (Math.abs(x - vers) < 0.3 && Math.abs(v) < 4) {
      surImage(vers);
      if (surFin) surFin();
      return;
    }
    surImage(x);
    id = requestAnimationFrame(pas);
  }

  id = requestAnimationFrame(pas);
  return () => {
    arrete = true;
    if (id != null) cancelAnimationFrame(id);
  };
}

/**
 * Point d'arrêt projeté d'un lancer, à la manière du défilement inertiel (fonction utilisée par
 * Apple dans « Designing Fluid Interfaces ») : un petit geste rapide porte loin.
 * @param {number} vitesse px/s
 */
export function projeter(vitesse, deceleration = 0.99) {
  return ((vitesse / 1000) * deceleration) / (1 - deceleration);
}

/** Résistance progressive au-delà d'une borne : plus on tire, moins l'élément suit. */
export function elastique(depassement, dimension, constante = 0.55) {
  return (depassement * dimension * constante) / (dimension + constante * Math.abs(depassement));
}

/** Vitesse (px/s) estimée sur les derniers ~100 ms d'un historique `{ x, y, t }`. */
export function vitesseRecente(historique) {
  if (historique.length < 2) return { vx: 0, vy: 0 };
  const dernier = historique[historique.length - 1];
  let premier = historique[0];
  for (let i = historique.length - 1; i >= 0; i -= 1) {
    if (dernier.t - historique[i].t > 100) break;
    premier = historique[i];
  }
  const dt = (dernier.t - premier.t) / 1000;
  if (dt <= 0) return { vx: 0, vy: 0 };
  return { vx: (dernier.x - premier.x) / dt, vy: (dernier.y - premier.y) / dt };
}
